// src/pages/Dashboard/Admin/DetailsAdherents/components/PalmaresPanel.jsx

import React, { useMemo } from "react";
import DashboardDataTable from "./DashboardDataTable";

import { formatDate, safeArray } from "../utils/adherentDashboardUtils";

function Badge({ children, className }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ${className}`}
    >
      {children}
    </span>
  );
}

function classementClass(classement) {
  const value = String(classement || "").toLowerCase();

  if (value === "1" || value.includes("1er") || value.includes("1ère")) {
    return "bg-yellow-50 text-yellow-700 ring-yellow-200 dark:bg-yellow-500/10 dark:text-yellow-300 dark:ring-yellow-500/20";
  }

  if (value === "2" || value.includes("2e")) {
    return "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700";
  }

  if (value === "3" || value.includes("3e")) {
    return "bg-orange-50 text-orange-700 ring-orange-200 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-500/20";
  }

  return "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20";
}

export default function PalmaresPanel({ palmares }) {
  const rows = useMemo(() => {
    return safeArray(palmares).sort(
      (a, b) =>
        new Date(b?.date_palmares || 0).getTime() -
        new Date(a?.date_palmares || 0).getTime()
    );
  }, [palmares]);

  return (
    <DashboardDataTable
      minWidth="950px"
      columns={[
        {
          key: "titre",
          label: "Palmarès",
          render: (row) => (
            <div>
              <p className="font-semibold text-slate-900 dark:text-white">
                🏆 {row?.titre_palmares || "Sans titre"}
              </p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {formatDate(row?.date_palmares, true)}
              </p>
            </div>
          ),
        },
        {
          key: "classement",
          label: "Classement",
          render: (row) =>
            row?.classement_palmares ? (
              <Badge className={classementClass(row.classement_palmares)}>
                {row.classement_palmares}
              </Badge>
            ) : (
              "—"
            ),
        },
        {
          key: "resultats",
          label: "Résultats",
          render: (row) => {
            const results = String(row?.resultats_palmares || "")
              .split("|")
              .map((item) => item.trim())
              .filter(Boolean);

            if (!results.length) return "—";

            return (
              <div className="flex flex-wrap gap-1.5">
                {results.map((item, index) => (
                  <Badge
                    key={`${item}-${index}`}
                    className={
                      item.includes("✅")
                        ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
                        : item.includes("❌")
                          ? "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20"
                          : "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700"
                    }
                  >
                    {item}
                  </Badge>
                ))}
              </div>
            );
          },
        },
        {
          key: "active",
          label: "État",
          render: (row) => (
            <Badge
              className={
                row?.is_active
                  ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
                  : "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700"
              }
            >
              {row?.is_active ? "Actif" : "Archivé"}
            </Badge>
          ),
        },
        {
          key: "observations",
          label: "Observations",
          render: (row) => row?.observations_palmares || "—",
        },
      ]}
      rows={rows}
      emptyMessage="Aucun palmarès disponible."
    />
  );
}